import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { BRAND } from '../../config/brand';
import Layout from '../../components/common/Layout/Layout';
import './Account.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_RULES = 'At least 8 characters, one uppercase letter, one number, and one special character';

function validateNewPassword(password) {
  if (!password || password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (!/[A-Z]/.test(password)) return PASSWORD_RULES;
  if (!/[0-9]/.test(password)) return PASSWORD_RULES;
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) return PASSWORD_RULES;
  return null;
}

function AccountChangePassword() {
  const { user, changePassword } = useAuth();
  const { showToast } = useToast();
  const hasPassword = user?.hasPassword !== false;
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (hasPassword && !formData.currentPassword.trim()) {
      setError('Please enter your current password');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(hasPassword ? 'New password and confirm password do not match' : 'Password and confirm password do not match');
      return;
    }

    const passwordError = validateNewPassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    const result = await changePassword(hasPassword ? formData.currentPassword : '', formData.newPassword);

    if (result.success) {
      setSuccess(true);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showToast(result.message || (hasPassword ? 'Your password has been updated. You\'re all set.' : 'Password set. You can now sign in with email and password.'), 'success');
    } else {
      setError(result.message || (hasPassword ? 'Failed to change password. Please try again.' : 'Failed to set password. Please try again.'));
    }

    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{hasPassword ? 'Change Password' : 'Set Password'} | {BRAND_NAME}</title>
        <meta name="description" content={hasPassword ? 'Change your account password securely.' : 'Set a password to sign in with email as well as Google.'} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account/change-password`} />
      </Helmet>

      <Layout showFooter showBottomTab>
        <div className="account-page">
          <div className="account-container">
            <div className="account-header">
              <Link to="/account" className="account-back">
                ← Back to Account
              </Link>
              <h1 className="account-title">{hasPassword ? 'Change Password' : 'Set Password'}</h1>
              <p className="account-subtitle">
                Enter your current password and choose a new one. Use a strong password you don’t use elsewhere.
              </p>
            </div>

            {success ? (
              <div className="account-message account-message--success" role="status">
                {hasPassword
                  ? 'Your password has been changed. Use your new password the next time you sign in.'
                  : 'Your password is set. You can now sign in with your email and password anytime.'}
              </div>
            ) : (
              <div className="account-settings">
                <form onSubmit={handleSubmit} className="account-settings-form">
                  {hasPassword && (
                    <div className="account-form-group">
                      <label htmlFor="currentPassword" className="account-form-label">
                        Current password
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleChange}
                        className="account-form-input"
                        required
                        autoComplete="current-password"
                        placeholder="••••••••"
                        aria-describedby={error ? 'change-password-error' : undefined}
                      />
                    </div>
                  )}

                  <div className="account-form-group">
                    <label htmlFor="newPassword" className="account-form-label">
                      {hasPassword ? 'New password' : 'Password'}
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className="account-form-input"
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      minLength={PASSWORD_MIN_LENGTH}
                      aria-describedby="new-password-hint"
                    />
                    <p id="new-password-hint" className="account-form-hint">
                      {PASSWORD_RULES}
                    </p>
                  </div>

                  <div className="account-form-group">
                    <label htmlFor="confirmPassword" className="account-form-label">
                      {hasPassword ? 'Confirm new password' : 'Confirm password'}
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="account-form-input"
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </div>

                  {error && (
                    <div id="change-password-error" className="account-message account-message--error" role="alert">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="account-form-submit"
                    disabled={loading}
                    aria-busy={loading}
                  >
                    {loading ? (hasPassword ? 'Changing password…' : 'Setting password…') : (hasPassword ? 'Change password' : 'Set password')}
                  </button>
                </form>
              </div>
            )}

            {success && (
              <Link to="/account" className="account-form-submit" style={{ display: 'inline-block', textAlign: 'center', marginTop: 16 }}>
                Back to Account
              </Link>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default AccountChangePassword;
